/*#
# b3d-controllable

Base class for any entity that can be driven by a `ControlInput` — bipeds, cars,
helicopters, boats, etc.

Subclasses override `applyInput(input, dt)` with their specific movement model.
The base class handles the update loop: poll input → apply input.

## Example

The base class is abstract — you subclass it. A minimal one that yaws to the throttle
(real subclasses — [b3d-biped](/b3d-biped/), [b3d-car](/b3d-car/),
[b3d-aircraft](/b3d-aircraft/) — put a full movement model here):

```javascript
import { B3dControllable } from 'tosijs-3d'

class Spinner extends B3dControllable {
  applyInput(input, dt) {
    // input is a ControlInput; act on the fields this entity cares about.
    // `ry` is its yaw in degrees (from AbstractMesh), applied to its mesh.
    this.ry += input.throttle * dt * 180
  }
}
```

## Key Methods

- `applyInput(input, dt)` — override with movement/animation logic
- `getCameraTarget()` — returns the node cameras should follow
- `handleGainFocus()` / `handleLoseFocus()` — lifecycle hooks for input switching
*/
/*{ "parent": "input-internals", "order": 20 }*/

import * as BABYLON from '@babylonjs/core'
import {
  AbstractMesh,
  simHalted,
  controlsLive,
  type SimGateOwner,
  sceneDelta,
} from './b3d-utils.js'
import type { B3d } from './tosi-b3d.js'
import { emptyInput } from './control-input.js'
import type { ControlInput, InputProvider } from './control-input.js'
import type { InputMapping } from './virtual-gamepad.js'
import { realNow } from './clock.js'

type FocusManager = {
  adoptIfVacant?: (e: B3dControllable) => void
  releaseFocus?: () => void
  focused?: B3dControllable | null
}

export class B3dControllable extends AbstractMesh {
  inputProvider: InputProvider | null = null
  inputMapping?: InputMapping
  /** Last polled input — read by the XR rig for camera zoom/peek intent. */
  lastInput: ControlInput | null = null
  protected lastUpdate = 0

  sceneReady(owner: B3d, scene: BABYLON.Scene) {
    super.sceneReady(owner, scene)
    // PULL, don't push. A controllable added mid-game (a RESPAWNED aircraft) announces
    // itself to the focus manager once IT is ready — rather than the manager guessing when
    // to look, or watching the subtree.
    //
    // This is not a style preference: the manager scans for `player: true` at ITS setup,
    // and a caller who appends an entity and immediately asks the manager to re-scan will
    // find `player` still false — tosijs drains attributes on connectedCallback, so the
    // flag isn't there yet at the moment of appendChild. (B3d abandoned MutationObserver
    // discovery for exactly this race; see CLAUDE.md.) By sceneReady the attributes are
    // drained, so this is the moment when the question can be answered truthfully.
    //
    // The manager only takes us if it's driving NOBODY — so this never steals the camera
    // from a live player; it only fills a vacancy, which is precisely the respawn case.
    const focus = this.closest('tosi-b3d-input-focus') as FocusManager | null
    this._focus = focus
    focus?.adoptIfVacant?.(this)
  }

  /** The focus manager we announced ourselves to (kept: once we are removed,
   * `closest()` can no longer find it). */
  private _focus: FocusManager | null = null

  sceneDispose() {
    /*
    A REMOVED ENTITY GIVES ITS SEAT BACK. Replacing the player (remove the
    old craft, append a new one) is the ordinary respawn shape, and focus used
    to keep holding the removed one: the new craft's adoptIfVacant saw an
    occupied seat, got no input, and b3d-death ignored its crash because death
    only mourns what focus drives (manta-recon; board #2421). The workaround
    was calling releaseFocus() first, which nobody would guess.
    */
    const focus = this._focus
    this._focus = null
    if (focus?.focused === this) focus.releaseFocus?.()
    this.inputProvider = null
    super.sceneDispose()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  applyInput(input: ControlInput, dt: number) {
    // Subclasses override this with their movement model
  }

  getCameraTarget(): BABYLON.Node | null {
    return this.mesh ?? null
  }

  protected _halted = false

  /**
   * **Stop simulating. You are a corpse.** Idempotent.
   *
   * **Belt and braces, honestly labelled.** A focus-managed entity already
   * stops when it dies, but only INCIDENTALLY: `releaseFocus()` sets
   * `inputProvider = null` and `_update` happens to short-circuit on that. I
   * went looking for a coasting corpse behind a real bug report and measured
   * one that does not move (0.05 m in 10 s) — the report turned out to be the
   * XR rig teleporting to the origin, not the wreck flying away.
   *
   * It stays because the incidental version does not cover everything: `die()`
   * can be handed an entity the focus manager never held — a scripted death, an
   * AI, a test — and nothing would stop that one at all. "You are dead" should
   * be something the entity is TOLD, not a side effect of a null field.
   *
   * Halting here rather than in each subclass because `_update` is the single
   * entry point every controllable's motion goes through.
   */
  halt(): void {
    this._halted = true
  }

  /** True once `halt()` has been called. */
  get halted(): boolean {
    return this._halted
  }

  /**
   * TRUE world velocity, m/s, or `null` if this entity does not track one.
   *
   * The seam exists because `b3d-death` has to launch a wreck on the velocity
   * it died with, and an entity's own `velocity` field is often only part of
   * the story — `b3d-aircraft`'s reads ZERO in wing-borne flight, because the
   * fly-by-wire path moves the node directly. A wreck given that would drop
   * straight down out of a 60 m/s dive.
   */
  getWorldVelocity(): BABYLON.Vector3 | null {
    return null
  }

  /**
   * A node carrying this entity's POSITION and HEADING, held level — what a
   * chase camera should be a child of.
   *
   * It exists so a chase rig can be **parented** rather than recomputed. The XR
   * rig runs in `onXRFrameObservable`, which fires BEFORE `scene.render()`,
   * while an entity moves in `registerBeforeRender`, which fires inside it — so
   * anything that copies a position is copying LAST frame's, every frame, and a
   * variable frame time turns that fixed lag into jitter. A child's world matrix
   * is resolved at render time, after the entity moved, so the order stops
   * mattering. (Measured 2026-08-26; see TODO → "THE CHASE RIG".)
   *
   * Level and yaw-only on purpose: parenting straight to the airframe would hand
   * the camera the attitude, which is a rolling horizon in a headset.
   *
   * `null` (the default) means "no anchor" and a caller must fall back.
   */
  getChaseAnchor(): BABYLON.TransformNode | null {
    return null
  }

  handleGainFocus() {
    this.inputProvider?.activate?.()
  }

  handleLoseFocus() {
    this.inputProvider?.deactivate?.()
  }

  protected _update = () => {
    /*
    WHERE REAL TIME MEETS WORLD TIME (board #3466).

    Input lives in real time: the keyboard, a stick and the pause menu keep
    working whatever the world is doing. The world takes input only as a
    snapshot at its own step, and steps by WORLD time (\`sceneDelta\`: zero
    while paused, scaled by \`timeScale\`). It used to step by real time, so
    a world in slow motion still moved its vehicles and bipeds at full speed.

    EVENTS CANNOT QUEUE. While the world is not running (paused, halted,
    unfocused), the provider is still read and the reading thrown away, so
    anything it accumulates between reads (a wheel's scroll) drains instead of
    arriving all at once on resume. Tonio: "We don't want the UI to lock up, we
    just don't want the simulation to do anything with it."
    */
    const scene = (this.owner as { scene?: BABYLON.Scene } | null)?.scene
    const dt = scene != null ? sceneDelta(scene) : 0
    this.lastUpdate = realNow()

    // HALT, don't just zero the stick: with no input an aircraft COASTS,
    // which is indistinguishable from cruising (#30).
    const stopped =
      simHalted(this.owner as SimGateOwner | null) || this._halted || dt <= 0
    if (stopped) {
      this.inputProvider?.poll(0)
      return
    }

    if (this.inputProvider == null) return
    // Scene input focus: when a page hosts multiple demos, only the active (last
    // hovered/clicked) scene consumes the shared keyboard/gamepad — an unfocused
    // scene sees neutral input so it idles instead of being driven in the background.
    /*
    `inputSuppressed` is a MODAL gate: a prompt that asks you to pull a trigger
    must not also fire the gun with it (the re-seat dialog). Unlike `frozen` it
    does NOT stop the world — an unfocused demo on a busy page should idle, not
    freeze — which is why the two predicates are separate. See `b3d-utils`.
    */
    const live = controlsLive(this.owner as SimGateOwner | null)
    // Read either way, so nothing builds up while the controls are not live.
    const reading = this.inputProvider.poll(dt)
    const input = live ? reading : emptyInput()
    this.lastInput = input
    this.applyInput(input, dt)
  }
}
